import React, { useEffect, useState } from "react";
import "../../styles/assettracking.css";
import axios from "axios";
import AssetDetailModal from "../assetdetailmodal";
import { API_BASE_URL } from "../../Config/config";

const AssetTracking = ({ updateTrackedAssetCount }) => {
  const [trackedAssets, setTrackedAssets] = useState([]);
  const [sortedAssets, setSortedAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSorted, setIsSorted] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/assets/tracking`);
        const assets = response.data.tracking_assets;
        setTrackedAssets(assets);
        setSortedAssets(assets);
        setFilteredAssets(assets);
        updateTrackedAssetCount(assets.length);
      } catch (error) {
        //console.error("Error fetching assets:", error);
      }
    };

    fetchAssets();
  }, [updateTrackedAssetCount]);

  // Handle search functionality
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);

    const assetsToFilter = isSorted ? sortedAssets : trackedAssets;

    if (!searchValue.trim()) {
      setFilteredAssets(assetsToFilter);
      return;
    }

    const filtered = assetsToFilter.filter((asset) => {
      const searchLower = searchValue.toLowerCase();
      return (
        asset.asset_name?.toLowerCase().includes(searchLower) ||
        asset.staff_name?.toLowerCase().includes(searchLower) ||
        asset.iridis_id?.toLowerCase().includes(searchLower) ||
        asset.staff_id?.toLowerCase().includes(searchLower) ||
        asset.role?.toLowerCase().includes(searchLower) ||
        asset.email?.toLowerCase().includes(searchLower)
      );
    });

    setFilteredAssets(filtered);
  };

  useEffect(() => {
    handleSearch(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedAssets, isSorted]);

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const day = date.getDate();
    const year = date.getFullYear();
    const month = date.toLocaleString("default", { month: "long" });

    // day suffix function
    const getDaySuffix = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${getDaySuffix(day)} ${month} ${year}`;
  };

  const handleSort = (criteria) => {
    let sorted;
    if (criteria === "with-return") {
      sorted = [...trackedAssets].filter((asset) => asset.return_date);
    } else if (criteria === "without-return") {
      sorted = [...trackedAssets].filter((asset) => !asset.return_date);
    }

    setSortedAssets(sorted);
    setIsSorted(true);
    setIsModalOpen(false);
  };

  const handleReturn = () => {
    setSortedAssets(trackedAssets);
    setIsSorted(false);
  };

  const handleCardClick = (asset) => {
    setSelectedAsset(asset);
    setIsDetailModalOpen(true);
  };

  const handleDownloadAll = () => {
    const csvRows = [];

    csvRows.push([
      "#",
      "Iridis ID",
      "Asset Name",
      "Staff Name",
      "Role",
      "Staff ID",
      "Phone Number",
      "Email",
      "Reason For Acquisition",
      "Acquisition Date",
      "Approving Authority",
      "Expected Return Date",
      "Return Date",
      "Initial Condition",
      "Return Condition",
    ]);

    sortedAssets.forEach((asset) => {
      csvRows.push([
        asset.tracking_id,
        asset.iridis_id,
        asset.asset_name,
        asset.staff_name,
        asset.role,
        asset.staff_id,
        asset.phone,
        asset.email,
        asset.reason_for_acquisition,
        formatDate(asset.acquisition_date),
        asset.approving_authority,
        formatDate(asset.expected_return_date),
        formatDate(asset.return_date),
        asset.initial_condition,
        asset.return_condition,
      ]);
    });

    const csvString = csvRows
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", "asset_tracking.csv");
    a.click();

    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="asset-tracking-container">
      <h2 className="asset-tracking-title mb-5">Asset Tracking</h2>
      <div className="controls-container">
        <div className="left-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <button
            className="filter-button"
            onClick={() => {
              if (isSorted) {
                handleReturn();
              } else {
                setIsModalOpen(true);
              }
            }}
          >
            {isSorted ? "Return" : "Sort"}
          </button>
        </div>

        <div className="right-controls">
          <button className="download-button" onClick={handleDownloadAll}>
            Download All
          </button>
        </div>
      </div>

      {/* Modal for sorting */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Sort By :</h3>
            <button
              className="sort-button"
              onClick={() => handleSort("with-return")}
            >
              Returned Assets
            </button>
            <button
              className="sort-button"
              onClick={() => handleSort("without-return")}
            >
              Assets Awaiting Return
            </button>
            <button
              className="close-button"
              onClick={() => setIsModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Asset Details Modal */}
      <AssetDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        asset={selectedAsset}
        formatDate={formatDate}
      />

      {/* Cards Layout */}
      <div className="cards-container">
        {filteredAssets.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm
                ? `No assets found matching "${searchTerm}"`
                : "Assigned assets from inventory will be tracked here."}
            </p>
          </div>
        ) : (
          filteredAssets.map((asset, index) => (
            <div
              key={index}
              className="asset-card"
              onClick={() => handleCardClick(asset)}
            >
              {/* Card Content */}
              <div className="card-content">
                <div className="card-field">
                  <span className="field-label">{asset.asset_name}</span>
                </div>

                <div className="card-field">
                  <span className="field-value">
                    Assigned to {asset.staff_name}
                  </span>
                </div>
              </div>

              {/* Right side with status and arrow */}
              <div className="card-right-section">
                <div
                  className={`status-badge ${
                    asset.return_date ? "returned" : "not-returned"
                  }`}
                >
                  {asset.return_date ? "Returned" : "Not-Returned"}
                </div>

                <div className="arrow-indicator">&gt;</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AssetTracking;
